import { RefreshCw, Search } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '@/api';
import type { PublicationPolicy, RdapPreview } from '@/api/areas/market';
import { Badge } from '@/components/Badge';
import { DataTable, Section, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatCount, formatRelative } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { EnumBadge, When } from '../../compliance/ui';
import { DISPOSITION } from '../shared';
import { policyLabel } from '@/lib/labels';

const SHOWN = {
  PUBLIC: { tone: 'ok' },
  REDACTED: { tone: 'warn' },
  CONTACT_FORM: { tone: 'info' },
  OMITTED: { tone: 'neutral' },
} as const;

type Tld = Awaited<ReturnType<typeof api.getRdapOverview>>['tlds'][number];
type PreviewField = RdapPreview['fields'][number];

function OnOff({ on }: { on: boolean }) {
  const { t } = useTranslation('compliance');
  return on ? (
    <Badge tone="ok">{t('rdap.on')}</Badge>
  ) : (
    <Badge tone="neutral" icon={false}>
      {t('rdap.off')}
    </Badge>
  );
}

function useMatchLabel() {
  const { t } = useTranslation('compliance');
  const td = useTranslation('domains').t as unknown as (key: string, o: { defaultValue: string }) => string;
  const tldClass = (c: string) => td(`vocab.tldClass.${c}`, { defaultValue: c });
  return (p: PublicationPolicy): string => {
    const m = p.match as {
      tld_classes?: string[];
      suffixes?: string[];
      registries?: string[];
      all_domains?: boolean;
    };
    const parts = [...(m.tld_classes ?? []).map((c) => tldClass(c)), ...(m.suffixes ?? []).map((s) => `.${s}`), ...(m.registries ?? [])];
    return m.all_domains ? t('rdap.allDomains') : parts.join(', ') || '—';
  };
}

function useRenderValue() {
  const { t } = useTranslation('compliance');
  return (v: unknown): string => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'boolean') return v ? t('rdap.preview.yes') : t('rdap.preview.no');
    if (Array.isArray(v))
      return (
        v.map((x) => (typeof x === 'object' && x && 'host' in x ? String((x as { host: unknown }).host) : typeof x === 'string' ? x : JSON.stringify(x))).join(', ') ||
        t('rdap.preview.emptyList')
      );
    return JSON.stringify(v);
  };
}

function Preview({ domain }: { domain: string }) {
  const { t } = useTranslation('compliance');
  const renderValue = useRenderValue();
  const result = useApi((signal) => api.previewRdap(domain, { signal }), [domain]);
  if (result.status === 'loading') return <Loading label={t('rdap.preview.loading')} />;
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} title={t('rdap.preview.error')} />;
  const p: RdapPreview = result.data;
  if (!p.found) {
    return <EmptyState title={t('rdap.preview.notFound', { name: p.query })}>{t('rdap.preview.notFoundHint')}</EmptyState>;
  }
  const columns: Column<PreviewField>[] = [
    {
      key: 'field',
      header: t('rdap.preview.col.field'),
      cell: (f) => <span className="font-mono text-xs">{f.field}</span>,
    },
    {
      key: 'shown',
      header: t('rdap.preview.col.shown'),
      cell: (f) => <EnumBadge group="shown" value={f.shown} tones={SHOWN} />,
    },
    {
      key: 'value',
      header: t('rdap.preview.col.value'),
      cell: (f) => (
        <span className="text-sm break-words">
          {f.shown === 'PUBLIC' && f.registrant ? (
            <span className="text-muted-foreground">{t('rdap.preview.personal')}</span>
          ) : (
            renderValue(f.value) || <span className="text-muted-foreground">—</span>
          )}
        </span>
      ),
    },
  ];
  return (
    <div className="space-y-3">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <strong>{p.unicode_name}</strong>
        {p.unicode_name !== p.ascii_name ? <span className="font-mono text-muted-foreground">({p.ascii_name})</span> : null}
        <span aria-hidden="true">·</span>
        {p.policy ? (
          <EntityRef label={policyLabel(p.policy.policy_key)} to={`/compliance/policies/${encodeURIComponent(p.policy.policy_key)}`} sub={p.policy.version} />
        ) : (
          <Badge tone="warn" title={t('rdap.preview.noPolicyHint')}>
            {t('rdap.preview.noPolicy')}
          </Badge>
        )}
        <span aria-hidden="true">·</span>
        <span className="text-muted-foreground">{t('rdap.preview.projected', { when: formatRelative(p.last_update) })}</span>
        <span aria-hidden="true">·</span>
        <span className="text-muted-foreground">{t('rdap.preview.redaction', { text: p.redaction_text })}</span>
      </p>
      <div className="rounded-md border">
        <DataTable columns={columns} rows={p.fields} rowKey={(f) => f.field} />
      </div>
    </div>
  );
}

/** `/compliance/icann/rdap`: RDAP switches per TLD, projection coverage, publication policies and a lookup preview (spec §38–§40). */
export function RdapPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const matchLabel = useMatchLabel();
  useDocumentTitle(t('rdap.title'));
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [input, setInput] = useState(q);
  const result = useApi((signal) => api.getRdapOverview({ signal }), []);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (input.trim()) p.set('q', input.trim());
    else p.delete('q');
    setParams(p);
  };

  const tldCols: Column<Tld>[] = [
    {
      key: 'tld',
      header: t('rdap.tcol.tld'),
      cell: (x) => <span className="font-mono font-semibold">.{x.zone}</span>,
    },
    {
      key: 'class',
      header: t('rdap.tcol.class'),
      cell: (x) => (
        <span className="text-sm">
          {x.tld_class}
          {x.icann_governed ? <span className="text-muted-foreground"> · ICANN</span> : null}
        </span>
      ),
    },
    {
      key: 'rdap',
      header: t('rdap.tcol.rdap'),
      cell: (x) => <OnOff on={x.rdap_enabled} />,
    },
    {
      key: 'whois',
      header: t('rdap.tcol.whois'),
      cell: (x) => <OnOff on={x.whois_enabled} />,
    },
    {
      key: 'records',
      header: t('rdap.tcol.projected'),
      numeric: true,
      cell: (x) => formatCount(x.records),
    },
    {
      key: 'servable',
      header: t('rdap.tcol.servable'),
      numeric: true,
      cell: (x) => formatCount(x.servable),
    },
    {
      key: 'pending',
      header: t('rdap.tcol.pending'),
      numeric: true,
      cell: (x) => (x.pending_refresh > 0 ? <Badge tone="pending">{formatCount(x.pending_refresh)}</Badge> : 0),
    },
    {
      key: 'last',
      header: t('rdap.tcol.last'),
      cell: (x) => <When at={x.last_projected_at} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('rdap.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.icann') }]}
        subtitle={t('rdap.subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-6">
        <Section title={t('rdap.preview.title')} description={t('rdap.preview.hint')}>
          <form className="mb-4 flex max-w-lg gap-2" onSubmit={submit}>
            <Input type="search" aria-label={t('rdap.preview.domain')} value={input} onChange={(e) => setInput(e.target.value)} placeholder="example.com" spellCheck={false} />
            <Button type="submit" disabled={!input.trim()}>
              <Search /> {t('rdap.preview.submit')}
            </Button>
          </form>
          {q ? <Preview key={q} domain={q} /> : null}
        </Section>

        {result.status === 'loading' ? <Loading rows={4} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('rdap.loadError')} /> : null}
        {result.status === 'success' ? (
          <>
            <Section title={t('rdap.tlds')} description={t('rdap.tldsHint')} flush>
              <DataTable columns={tldCols} rows={result.data.tlds} rowKey={(x) => x.zone} empty={<EmptyState title={t('rdap.noTlds')} />} />
            </Section>
            <Section title={t('rdap.policies')} description={t('rdap.policiesHint')} flush>
              {result.data.publication_policies.length === 0 ? (
                <div className="px-5 pb-5">
                  <EmptyState title={t('rdap.noPolicies')}>{t('rdap.noPoliciesHint')}</EmptyState>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40 text-left">
                        <th scope="col" className="px-4 py-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                          {t('rdap.field')}
                        </th>
                        {result.data.publication_policies.map((p) => (
                          <th key={`${p.policy_key}@${p.version}`} scope="col" className="px-4 py-2 align-top font-normal">
                            <EntityRef label={policyLabel(p.policy_key)} to={`/compliance/policies/${encodeURIComponent(p.policy_key)}`} />
                            <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                              v{p.version} · {matchLabel(p)} {p.in_force ? <Badge tone="ok">{t('rdap.inForce')}</Badge> : <Badge tone="neutral">{t('rdap.notInForce')}</Badge>}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {result.data.fields.map((f) => (
                        <tr key={f} className="hover:bg-muted/40">
                          <td className="px-4 py-2 font-mono text-xs">{f}</td>
                          {result.data.publication_policies.map((p) => (
                            <td key={`${p.policy_key}@${p.version}`} className="px-4 py-2">
                              <EnumBadge group="disposition" value={p.fields[f] ?? 'OMIT'} tones={DISPOSITION} />
                            </td>
                          ))}
                        </tr>
                      ))}
                      <tr>
                        <td className="px-4 py-2 text-sm">{t('rdap.redactionText')}</td>
                        {result.data.publication_policies.map((p) => (
                          <td key={`${p.policy_key}@${p.version}`} className="px-4 py-2 text-sm">
                            {p.redaction_text ?? '—'}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </>
        ) : null}
      </div>
    </>
  );
}
